import * as TS from '@/styles/table.styles';
import React, { useState } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

const CallingNumberInputContainer = styled.div`
  table {
    border-top: 0;
  }
  th {
    font-weight: 600;
  }
  width: 100%;
  @media screen and (max-width: 540px) {
    th,
    td,
    input {
      font-size: 1.6rem;
    }
    td {
      span {
        display: block;
        margin-top: 1rem;
        font-size: 1.4rem;
      }
      textarea {
        font-size: 1.6rem;
      }
    }
  }
`;

const FileFomatWrap = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const PhoneNumberInput = styled.input`
  cursor: pointer;
  width: 100%;
  max-width: 31rem;
  padding: 0.5rem;
  border: none;
  text-align: start;
  font-size: 1.2rem;
  color: ${(props) => props.theme.colors.txt500};
  ::placeholder {
    color: ${(props) => props.theme.colors.placeholder};
  }
  @media screen and (max-width: 640px) {
    max-width: 100%;
  }
`;

const PhoneNumberTextarea = styled.textarea`
  width: 100%;
  height: 100%;
  min-height: 10rem;
  padding: 0.5rem;
  border: none;
  background-color: ${(props) => props.theme.colors.gray200};
  text-align: start;
  font-size: 1.2rem;
  color: ${(props) => props.theme.colors.txt500};
  /* font-weight: lighter; */
  resize: none;
`;

const FileListContainer = styled.div`
  width: 50%;
  margin-top: 1rem;
  font-size: 1.2rem;
  color: ${(props) => props.theme.colors.txt500};
  border: 1px solid ${(props) => props.theme.colors.black};
`;

const FileListWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.6rem;
  gap: 1rem;
  color: ${(props) => props.theme.colors.txt500};
  &:hover {
    background-color: ${(props) => props.theme.colors.gray300};
  }
  button {
    background-color: ${(props) => props.theme.colors.main};
    color: ${(props) => props.theme.colors.buttonText};
    font-size: 1.2rem;
    padding: 0.2rem 0.4rem;
    border-radius: 0.5rem;
  }
`;

const ValidationWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-direction: column;
`;

const ValidationError = styled.span`
  font-size: 1.2rem;
  color: red;
  padding: 0.4rem;
`;

const MAX_FILE_COUNT = 5; // 최대 파일 개수
const MAX_TOTAL_SIZE_MB = 10; // 최대 총 용량 (MB)

const CallingNumberInput = ({ register, setValue, errors }: { register: any; setValue: any; errors: any }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string>('');
  const [totalSizeMB, setTotalSizeMB] = useState<number>(0); // 파일의 총 용량(MB)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = e.target.files ? Array.from(e.target.files) : [];

    // 새로운 파일 추가 후 전체 파일 개수 체크
    if (files.length + newFiles.length > MAX_FILE_COUNT) {
      setFileError(`파일은 최대 ${MAX_FILE_COUNT}개까지 업로드 가능합니다.`);
      return;
    }

    // 새로운 파일들의 총 용량 체크
    const totalSize = [...files, ...newFiles].reduce((acc, file) => acc + file.size, 0);
    if (totalSize > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
      setFileError(`총 파일 용량은 ${MAX_TOTAL_SIZE_MB}MB를 초과할 수 없습니다.`);
      return;
    }

    // 오류 없으면 파일 추가
    const updatedFiles = [...files, ...newFiles];
    setFiles(updatedFiles);
    setFileError(''); // 오류 메시지 초기화

    // 파일들의 총 용량(MB) 계산
    const newTotalSizeMB = (totalSize / 1024 / 1024).toFixed(2);
    setTotalSizeMB(parseFloat(newTotalSizeMB)); // 반올림된 값을 상태에 저장

    // Update the form value
    // register('file', { value: [...files, ...newFiles] });
    setValue('file', updatedFiles);
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    setFileError(''); // 오류 메시지 초기화

    // 파일들의 총 용량(MB) 다시 계산
    const newTotalSizeMB = updatedFiles.reduce((acc, file) => acc + file.size, 0) / 1024 / 1024;
    setTotalSizeMB(parseFloat(newTotalSizeMB.toFixed(2))); // 반올림된 값을 상태에 저장

    // Update the form value
    // register('file', updatedFiles);
    setValue('file', updatedFiles);
  };

  return (
    <CallingNumberInputContainer className="callingNumberInput">
      <TS.MyPageTable className="borTop0 thBorderLeft thBackStyles">
        <tbody>
          <tr>
            <th>등록할 발신번호</th>
            <td>
              <PhoneNumberInput
                id="postcallnumber"
                type="text"
                maxLength={11}
                onChange={() => {}}
                placeholder="숫자만 입력해주세요."
                {...register('callbackNo')}
              />
              <ValidationError>{errors.callbackNo?.message}</ValidationError>
            </td>
          </tr>

          <tr>
            <th>결과통지번호</th>
            <td>
              <PhoneNumberInput
                id="postalarmPhone"
                name="alarmPhone"
                type="text"
                maxLength={11}
                onChange={() => {}}
                placeholder="숫자만 입력해주세요."
                {...register('alarmPhone')}
              />
              <ValidationError>{errors.alarmPhone?.message}</ValidationError>
            </td>
          </tr>

          <tr>
            <th>등록할 서류</th>
            <td>
              <FileFomatWrap>
                <ValidationWrap>
                  <PhoneNumberInput
                    id="postfile"
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png,.tif,.pdf,.zip,.hwp,.txt,.docx"
                    onChange={handleFileChange}
                  />
                  {fileError && <ValidationError>{fileError}</ValidationError>}
                </ValidationWrap>
                <ValidationWrap>
                  <ValidationError>
                    {MAX_TOTAL_SIZE_MB}MB 이하 파일 첨부 가능({totalSizeMB} MB)
                  </ValidationError>
                  {/* <ValidationError>15MB 이하 파일 첨부 가능({totalSizeMB} MB)</ValidationError> */}
                </ValidationWrap>
              </FileFomatWrap>
              <FileListContainer>
                {files.map((file, index) => (
                  <FileListWrap key={uuidv4()}>
                    {file.name}{' '}
                    <button type="button" onClick={() => handleRemoveFile(index)}>
                      삭제
                    </button>
                  </FileListWrap>
                ))}
              </FileListContainer>
            </td>
          </tr>

          <tr>
            <th style={{ verticalAlign: 'middle' }}>메모</th>
            <td>
              <PhoneNumberTextarea
                id="postremark"
                onChange={() => {}}
                {...register('remark')}
                // placeholder="숫자만 입력해주세요."
              />
            </td>
          </tr>
        </tbody>
      </TS.MyPageTable>
    </CallingNumberInputContainer>
  );
};

export default CallingNumberInput;
